import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Typography,
  Modal,
  Space,
  Table, // Add Table import
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import React, { Fragment, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  addSingleAward,
  AwardEventsDetails,
  AwardTypesConfigurationDetails,
} from "../../redux/rtk/features/award/awardSlice";
import AddAwardType from "./AddAwardType";

const { Option } = Select;

const AddAward = ({ drawer, formOnly, tableOnly, onCancel }) => {
  const awardType = useSelector((state) => state?.award?.awardType);
  const AwardEvents = useSelector((state) => state?.award?.AwardEvents);

  // console.log("awardType", awardType);

  const [loader, setLoader] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(AwardEventsDetails());
    dispatch(AwardTypesConfigurationDetails());
  }, [dispatch]);

  const { Title } = Typography;

  const onFinish = async (values) => {
    // console.log(values);

    setLoader(true);
    try {
      const awardData = {
        AwardEventId: 0,
        AwardTypeId: values.AwardTypeId,
        AwardEventName: values.AwardEventName,
        AwardEventDescription: values.AwardEventDescription,
        AwardEventDate: values.AwardEventDate
          ? dayjs(values.AwardEventDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        DataStatus: 1,
      };

      // console.log("data>", awardData);

      const resp = await dispatch(addSingleAward(awardData));

      if (resp.payload.message === "success") {
        setLoader(false);
        form.resetFields();
        dispatch(AwardEventsDetails());
        toast.success("Award added successfully");
        onCancel?.();
      } else {
        setLoader(false);
        toast.error("Failed to add award");
      }
    } catch (error) {
      setLoader(false);
      toast.error("An error occurred while adding the award");
      // console.error(error);
    }
  };

  const onFinishFailed = (errorInfo) => {
    toast.warning("Failed at adding award");
    setLoader(false);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  // Table columns definition
  const columns = [
    {
      title: "Award Name",
      dataIndex: "AwardEventName",
      key: "AwardEventName",
    },
    {
      title: "Description",
      dataIndex: "AwardEventDescription",
      key: "AwardEventDescription",
    },
    {
      title: "Award Date",
      dataIndex: "AwardEventDate",
      key: "AwardEventDate",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      title: "Frequency",
      dataIndex: "AwardFrequencyName",
      key: "AwardFrequencyName",
    },
    {
      title: "Awarded",
      dataIndex: "IsDistributed",
      key: "IsDistributed",
      render: (isDistributed) => (isDistributed ? "Yes" : "No"),
    },
  ];

  return (
    <div className="add-award-container">
      <Row className="award-row" justify={drawer ? "center" : "space-between"} gutter={16}>
        <>
        {!tableOnly && (
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={drawer ? 22 : formOnly ? 24 : 8}
          xl={drawer ? 22 : formOnly ? 24 : 8}
          className="column-design border rounded card-custom award-form-col"
        >
          <Title level={4} className="m-2 mt-2 mb-5 text-center">
            Add Award
          </Title>
          <Form
            style={{ marginBottom: "40px" }}
            form={form}
            name="basic"
            labelCol={{
              span: 6,
            }}
            wrapperCol={{
              span: 12,
            }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            initialValues={{
              AwardEventDate: dayjs(),
            }}
          >
            <Form.Item
              style={{ marginBottom: "10px" }}
              label="Award Type"
              required
            >
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name="AwardTypeId"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: "Please select an award type!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Award Type"
                    style={{ width: "calc(100% - 40px)" }}
                  >
                    {awardType &&
                      awardType.map((type) => (
                        <Option key={type.AwardTypeId} value={type.AwardTypeId}>
                          {type.AwardTypeName}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={showModal}
                  style={{ width: "40px" }}
                />
              </Space.Compact>
            </Form.Item>

            <Form.Item
              style={{ marginBottom: "10px" }}
              label="Name"
              name="AwardEventName"
              rules={[
                {
                  required: true,
                  message: "Please input the award name!",
                },
              ]}
            >
              <Input placeholder="Employee Of The Month" />
            </Form.Item>

            <Form.Item
              style={{ marginBottom: "20px" }}
              label="Description"
              name="AwardEventDescription"
              rules={[
                {
                  required: true,
                  message: "Please input the award description!",
                },
              ]}
            >
              <Input placeholder="Employee Who Performed Well" />
            </Form.Item>

            <Form.Item
              style={{ marginBottom: "10px" }}
              label="Award Date"
              name="AwardEventDate"
              rules={[
                {
                  required: true,
                  message: "Please select the award date!",
                },
                {
                  validator(_, value) {
                    if (!value || dayjs(value).isValid()) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Please select a valid date!")
                    );
                  },
                },
              ]}
            >
              <DatePicker
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item
              style={{ marginBottom: "10px" }}
              wrapperCol={{
                offset: 6,
                span: 12,
              }}
            >
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                block
                loading={loader}
              >
                Add New Award
              </Button>
            </Form.Item>
          </Form>
        </Col>
        )}
        {(tableOnly || !formOnly) && (
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={drawer ? 22 : tableOnly ? 24 : 16}
          xl={drawer ? 22 : tableOnly ? 24 : 16}
          className="column-design border rounded card-custom award-table-col"
        >
          <Title level={4} className="m-2 mt-2 mb-5 text-center award-table-header">
            Award Events
          </Title>
          <div className="award-table-wrapper">
            <Table
              columns={columns}
              dataSource={AwardEvents}
              rowKey="AwardEventId"
              pagination={false}
              scroll={{ x: 600, y: "100%" }}
              bordered
            />
          </div>
        </Col>
        )}
        </>
      </Row>

      <Modal
        title="Add New Award Type"
        visible={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
      >
        <AddAwardType
          onSuccess={() => {
            setIsModalVisible(false);
            dispatch(AwardTypesConfigurationDetails());
          }}
        />
      </Modal>
    </div>
  );
};

export default AddAward;